// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo } from "react";
import IntlMessages from "Util/IntlMessages";
import { useIntl } from "react-intl";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Progress,
  Button
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import ReactTable from "react-table";
import DataTablePagination from "Components/DataTables/pagination";
import Chart from "react-apexcharts";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { useKpiSparklineLineStyle } from "Hooks/useKpiSparklineLineStyle";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import { addCommas } from "Util/Utils";
import data from "Data/dashboardSubscriptionPlg.json";

import "react-table/react-table.css";

function SubscriptionPlgDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);
  const kpiSparkLineStyle = useKpiSparklineLineStyle(colors.ReactifyChartColor1);
  const intl = useIntl();

  const funnelOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "80%",
          distributed: true,
          borderRadius: 4
        }
      },
      colors: [
        c.ReactifyChartColor1,
        c.ReactifyChartColor2,
        c.ReactifyChartColor3,
        c.ReactifyChartColor4,
        c.ReactifyChartColor5
      ],
      dataLabels: {
        enabled: true,
        formatter: (val, ctx) => {
          const i = ctx.dataPointIndex;
          if (i === 0) return addCommas(val);
          const prev = data.trialFunnel[i - 1].value;
          const pct = Math.round((val / prev) * 1000) / 10;
          return `${addCommas(val)} (${pct}%)`;
        }
      },
      xaxis: {
        categories: data.trialFunnel.map(f => f.step),
        labels: { style: { colors: c.primaryColor } }
      },
      grid: { borderColor: c.separatorColor },
      legend: { show: false },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const featureOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "75%" } },
      colors: [c.ReactifyChartColor2],
      dataLabels: { enabled: true, formatter: v => `${v}%` },
      xaxis: {
        categories: data.featureAdoption.map(f => f.feature),
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: { labels: { style: { colors: c.primaryColor } } },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const growthOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: [3, 3], curve: "smooth" },
      colors: [c.ReactifyChartColor1, c.ReactifyChartColor5],
      dataLabels: { enabled: false },
      legend: { position: "top" },
      xaxis: {
        categories: data.growthTrend.categories,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => `$${v}k`
        }
      },
      grid: { borderColor: c.separatorColor, strokeDashArray: 4 },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const csatOptions = useMemo(() => {
    const c = colors;
    return {
      labels: data.csat.map(x => x.label),
      colors: [c.ReactifyChartColor1, c.ReactifyChartColor3, c.ReactifyChartColor5],
      legend: { position: "bottom" },
      plotOptions: { pie: { donut: { size: "65%" } } },
      dataLabels: { enabled: true }
    };
    }, [colors]);

  const accountsColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "plg.col.account" }),
        accessor: "name",
        Cell: p => <span className="font-weight-medium">{p.value}</span>
      },
      {
        Header: formatMessage({ id: "plg.col.plan" }),
        accessor: "plan",
        Cell: p => <Badge color="light" className="text-dark">{p.value}</Badge>
      },
      {
        Header: formatMessage({ id: "plg.col.mrr" }),
        accessor: "mrr",
        Cell: p => `$${addCommas(p.value)}`
      },
      {
        Header: formatMessage({ id: "plg.col.health" }),
        accessor: "health",
        Cell: p => (
          <Badge color={p.value === "risk" ? "warning" : "success"}>
            {p.value}
          </Badge>
        )
      },
      {
        Header: formatMessage({ id: "plg.col.stage" }),
        accessor: "stage",
        Cell: p => <span className="text-capitalize">{p.value}</span>
      }
    ];
    }, [intl]);

  const k = data.kpis;

  return (
      <div className="dashboard-vertical dashboard-vertical--plg">
        <Fragment>
          <Row>
            <Colxx xxs="12">
              <BreadcrumbContainer
                heading={<IntlMessages id="menu.subscription-plg" />}
              />
              <Separator className="mb-5" />
            </Colxx>
          </Row>

          <Row className="mb-2">
            <Colxx md="6" lg="4" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light">
                <CardBody className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-0 small">
                        <IntlMessages id="plg.hero.nps" />
                      </p>
                      <h3 className="mb-0">{k.nps}</h3>
                    </div>
                    <Button color="primary" size="sm" outline>
                      <IntlMessages id="plg.hero.survey" />
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx md="6" lg="4" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light">
                <CardBody className="py-3">
                  <p className="text-muted mb-0 small">
                    <IntlMessages id="plg.hero.trials" />
                  </p>
                  <h3 className="mb-0">{addCommas(k.activeTrials)}</h3>
                  <span className="text-success small">+{k.trialsDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx md="12" lg="4" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light h-100">
                <CardBody className="py-3">
                  <p className="text-muted mb-0 small">
                    <IntlMessages id="plg.hero.activation" />
                  </p>
                  <h3 className="mb-0">{k.activated7dPct}%</h3>
                  <Progress value={k.activated7dPct} className="mt-2" />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <Sparklines data={data.mrrSpark} margin={6}>
                    <SparklinesLine style={kpiSparkLineStyle} />
                  </Sparklines>
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="plg.kpi.mrr" />
                  </p>
                  <h4 className="mb-0">${addCommas(k.mrr)}</h4>
                  <span className="text-success small">+{k.mrrDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-line-chart text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="plg.kpi.arr" />
                  </p>
                  <h4 className="mb-0">${addCommas(k.arr)}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-user-times text-warning mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="plg.kpi.churn" />
                  </p>
                  <h4 className="mb-0">{k.churnPct}%</h4>
                  <span className="text-success small">{k.churnDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-level-up text-success mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="plg.kpi.expansion" />
                  </p>
                  <h4 className="mb-0">${addCommas(k.expansionMrr)}</h4>
                  <span className="text-success small">+{k.expansionDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="plg.chart.funnel" />
                  </CardTitle>
                  <Chart
                    options={funnelOptions}
                    series={[{ data: data.trialFunnel.map(f => f.value) }]}
                    type="bar"
                    height={340}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="7" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="plg.chart.growth" />
                  </CardTitle>
                  <Chart
                    options={growthOptions}
                    series={[
                      { name: "New MRR ($k)", data: data.growthTrend.newMrr },
                      { name: "Churn MRR ($k)", data: data.growthTrend.churnMrr }
                    ]}
                    type="line"
                    height={340}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="6" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="plg.chart.features" />
                  </CardTitle>
                  <Chart
                    options={featureOptions}
                    series={[
                      {
                        name: "Adoption",
                        data: data.featureAdoption.map(f => f.pct)
                      }
                    ]}
                    type="bar"
                    height={280}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="plg.chart.csat" />
                  </CardTitle>
                  <Chart
                    options={csatOptions}
                    series={data.csat.map(x => x.value)}
                    type="donut"
                    height={260}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="3" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="plg.section.milestones" />
                  </CardTitle>
                  <ul className="list-unstyled mb-0 small">
                    {data.milestones.map((m, i) => (
                      <li key={i} className="mb-3 pb-2 border-bottom">
                        <div className="font-weight-medium">{m.title}</div>
                        <div className="text-muted">
                          {m.due} · {m.owner}
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx xxs="12" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="plg.table.accounts" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.accounts}
                    columns={accountsColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>
        </Fragment>
      </div>
    );
}

export default SubscriptionPlgDashboard;
